import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { analyzeVoice } from '@/lib/api';
import { toast } from 'sonner';

const VoiceRecorderModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!open) {
      stopRecording();
      setAnalysisResult(null);
      setError(null);
    }
  }, [open]);

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Detect supported audio format
      let mimeType = 'audio/webm';
      let audioFormat = 'webm';
      if (typeof MediaRecorder.isTypeSupported === 'function') {
        if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
          audioFormat = 'webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
          audioFormat = 'mp4';
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = 'audio/ogg';
          audioFormat = 'ogg';
        } else {
          mimeType = '';
          audioFormat = 'wav';
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      // Store the format for later use
      mediaRecorderRef.current._audioFormat = audioFormat;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/wav' });
        await processAudio(blob, mediaRecorderRef.current._audioFormat);
      };

      mediaRecorder.start(1000); // Collect chunks every second
      setIsRecording(true);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('No se pudo acceder al microfono. Verifica los permisos.');
      toast.error('Error al acceder al microfono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const processAudio = async (blob, format = 'webm') => {
    if (blob.size < 1000) {
      setError('La grabacion fue demasiado corta. Intenta de nuevo.');
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        
        try {
          const response = await analyzeVoice(base64, format);
          setAnalysisResult(response.data);
          toast.success('Audio procesado correctamente');
        } catch (err) {
          console.error('Error analyzing voice:', err);
          const detail = err?.response?.data?.detail || '';
          setError(`Error al procesar el audio. ${detail ? detail : 'Intenta de nuevo.'}`);
          toast.error('Error al procesar el audio');
        } finally {
          setIsProcessing(false);
        }
      };
      reader.onerror = () => {
        setError('Error al leer el archivo de audio.');
        setIsProcessing(false);
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error('Error processing audio:', err);
      setError('Error al procesar el audio');
      setIsProcessing(false);
    }
  };

  const handleCreateTask = () => {
    if (analysisResult) {
      navigate('/tasks/new', { state: { analysisData: analysisResult } });
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="voice-recorder-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900">
            Voz a Tarea
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center py-8">
          {/* Recording Button */}
          {!analysisResult && !isProcessing && (
            <>
              <button
                onClick={isRecording ? stopRecording : startRecording}
                data-testid="record-btn"
                className={`w-24 h-24 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isRecording 
                    ? 'bg-red-600 hover:bg-red-700 animate-pulse shadow-lg shadow-red-600/50' 
                    : 'bg-slate-900 hover:bg-slate-800 shadow-lg'
                }`}
              >
                {isRecording ? (
                  <MicOff className="w-10 h-10 text-white" />
                ) : (
                  <Mic className="w-10 h-10 text-white" />
                )}
              </button>
              <p className="mt-4 text-sm text-slate-600">
                {isRecording ? 'Grabando... Pulsa para detener' : 'Pulsa para grabar'}
              </p>
              {isRecording && (
                <div className="mt-4 flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1 bg-red-500 rounded-full animate-pulse"
                      style={{
                        height: `${Math.random() * 24 + 8}px`,
                        animationDelay: `${i * 0.1}s`
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Processing */}
          {isProcessing && (
            <div className="flex flex-col items-center">
              <Loader2 className="w-16 h-16 text-slate-900 animate-spin" />
              <p className="mt-4 text-sm text-slate-600">Procesando audio con IA...</p>
            </div>
          )}

          {/* Error */}
          {error && !isProcessing && (
            <div className="flex flex-col items-center text-center">
              <AlertCircle className="w-16 h-16 text-red-500" />
              <p className="mt-4 text-sm text-red-600">{error}</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setError(null)}
              >
                Intentar de nuevo
              </Button>
            </div>
          )}

          {/* Result */}
          {analysisResult && (
            <div className="w-full space-y-4">
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Analisis completado</span>
              </div>
              
              <div className="bg-stone-50 rounded-lg p-4 space-y-3">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Titulo</span>
                  <p className="text-slate-900 font-medium">{analysisResult.title}</p>
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Descripcion</span>
                  <p className="text-slate-600 text-sm">{analysisResult.description}</p>
                </div>
                <div className="flex gap-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Area</span>
                    <p className="text-slate-900">{analysisResult.functional_area}</p>
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Prioridad</span>
                    <p className="text-slate-900">{analysisResult.priority}</p>
                  </div>
                </div>
                {analysisResult.raw_transcription && (
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Transcripcion</span>
                    <p className="text-slate-500 text-sm italic">"{analysisResult.raw_transcription}"</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setAnalysisResult(null);
                    setError(null);
                  }}
                >
                  Nueva grabacion
                </Button>
                <Button
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  onClick={handleCreateTask}
                  data-testid="create-task-from-voice-btn"
                >
                  Crear Tarea
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VoiceRecorderModal;
